import InfoIcon from '@mui/icons-material/Info';
import { Grid, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { getBusinessRelation } from 'lib/api';
import { useBusinessInvoices } from 'lib/api/business';
import type { InvoiceRow } from 'lib/api/business/types';
import { getBlockedInvoices } from 'lib/api/express';
import CessionHistoryDialog from 'lib/common/Dialogs/CessionHistoryDialog';
import { Filter } from 'lib/common/GlobalSearcher';
import CustomAlert from 'lib/designSystem/Alert';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { CustomButtonProps } from 'lib/designSystem/HeaderWithActions';
import PageHeader from 'lib/designSystem/PageHeader';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import {
  formatDate,
  showAlert,
  validateCompanyTypes,
} from 'lib/functions/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import ShoppingDialogRecieved from '../../../../../../Finance/Components/Financing/Factoring/components/Recieved/components/ShoppingDialog';
import ReEnrollment from '../../../../../../PruffConnect/ReEnrollment';
import AddInvoicesManually from '../../components/addInvoices';
import ShoppingCartFactoring from '../../components/ShoppingCart';
import { buildBusinessInvoicesQuery } from '../../utils/queryBuilder';
import {
  getCessionHistoryColumnConfig,
  useCessionHistoryDialog,
} from '../../utils/useCessionHistoryDialog';

const PAGE_SIZE = 10;
const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: PAGE_SIZE,
  total: 0,
  totalPages: 0,
};

const BusinessInvoicesEmitted = (props: any) => {
  const { business, id } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const businessIdentifier = id ?? user?.businessIdentifier;

  const [inputFilters, setInputFilters] = useState({
    startDate: null as string | null,
    endDate: null as string | null,
    folio: '',
    stakeholderIdentifier: '',
    stakeholderName: '',
  });

  const [searchState, setSearchState] = useState({
    filters: {
      startDate: null as string | null,
      endDate: null as string | null,
      folio: '',
      stakeholderIdentifier: '',
      stakeholderName: '',
    },
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [selectedDocuments, setSelectedDocuments] = React.useState<
    InvoiceRow[]
  >([]);
  const [openShoppingCart, setOpenShoppingcart] = useState(false);
  const [continueToSimulation, setContinueToSimulation] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>({});
  const [blockedFolios, setBlockedFolios] = useState<string[]>([]);
  const [displayInvoices, setDisplayInvoices] = useState<InvoiceRow[]>([]);
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);
  const [successText, setSuccesText] = useState('');
  const [failedMessage, setFailedText] = useState('');
  const [buttonsData, setButtonsData] = useState<CustomButtonProps[]>([]);
  const [recentlyCreated, setRecentlyCreated] = useState(false);
  const [onlySimulation, setOnlySimulation] = useState(true);
  const [openAddInvoices, setOpenAddInvoices] = useState(false);
  const [expressNotEnrolled, setExpressNotEnrolled] = useState(false);

  const {
    openCessionHistoryDialog,
    setOpenCessionHistoryDialog,
    selectedCessions,
    handleShowCessionHistory,
  } = useCessionHistoryDialog(displayInvoices);

  const query = buildBusinessInvoicesQuery(searchState.filters, {
    page: searchState.page,
    pageSize: searchState.pageSize,
  });
  const { data, isLoading, isFetching, error } = useBusinessInvoices(
    dispatch,
    businessIdentifier,
    'emitted',
    query,
  );

  useEffect(() => {
    if (error && user?.express) {
      showAlert(
        dispatch,
        'error',
        `${user.companyName} RUT ${user.businessIdentifier} ya no se encuentra enrolado en Pruff.`,
      );
      setExpressNotEnrolled(true);
    }
  }, [
    error,
    user?.express,
    user?.companyName,
    user?.businessIdentifier,
    dispatch,
  ]);

  const blockInvoicesInOperations = useCallback(
    async (invoices: InvoiceRow[]): Promise<InvoiceRow[]> => {
      if (!user?.express) {
        setBlockedFolios([]);
        return invoices;
      }
      const invoicesFolios = invoices.map((x) => x.folio);
      const blocked = await getBlockedInvoices(
        dispatch,
        user.clientId as string,
        invoicesFolios,
      );
      const withBlockStatus = invoices.map((x) => ({
        ...x,
        status: blocked.includes(x.folio) ? 'Solicitud' : x.status,
      }));
      setBlockedFolios(blocked);
      return withBlockStatus;
    },
    [dispatch, user?.express, user?.clientId],
  );

  const executeExpressMainSetup = useCallback(
    async (invoices: InvoiceRow[], bizId: string): Promise<InvoiceRow[]> => {
      const businessRelationResponse = await getBusinessRelation(
        dispatch,
        bizId,
      );
      const isRecent =
        new Date(businessRelationResponse?.createdAt) >
        new Date(Date.now() - 300 * 1000);
      if (isRecent) setRecentlyCreated(true);
      return blockInvoicesInOperations(invoices);
    },
    [dispatch, blockInvoicesInOperations],
  );

  useEffect(() => {
    if (!data?.data || !businessIdentifier) return;
    if (user?.express) {
      void executeExpressMainSetup(data.data, businessIdentifier).then(
        setDisplayInvoices,
      );
    } else {
      setDisplayInvoices(data.data);
    }
  }, [data?.data, businessIdentifier, user?.express, executeExpressMainSetup]);

  const searchEmmitedInvoices = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      setSearchState((prev) => ({ ...prev, page, pageSize }));
      return {
        data: displayInvoices,
        pagination: data?.pagination ?? DEFAULT_PAGINATION,
      };
    },
    [data?.pagination, displayInvoices],
  );

  const triggerSearch = useCallback(async () => {
    setSearchState({
      filters: { ...inputFilters },
      page: 1,
      pageSize: PAGE_SIZE,
    });
  }, [inputFilters]);

  const setSuccessOpen = (text: string) => {
    setSuccess(true);
    setSuccesText(text);
  };

  const setFailureOpen = (text: string) => {
    setFailed(true);
    setFailedText(text);
  };

  const handleContinueToSimulation = () => {
    setContinueToSimulation(true);
  };

  const filters: Filter[] = [
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: inputFilters.startDate,
      setter: (v: string | null) =>
        setInputFilters((prev) => ({ ...prev, startDate: v })),
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: inputFilters.endDate,
      setter: (v: string | null) =>
        setInputFilters((prev) => ({ ...prev, endDate: v })),
      dateKey: 'endDate',
    },
    {
      label: 'Folio',
      type: 'text',
      value: inputFilters.folio,
      setter: (v: string) => setInputFilters((prev) => ({ ...prev, folio: v })),
    },
    {
      label: 'Rut receptor',
      type: 'text',
      value: inputFilters.stakeholderIdentifier,
      setter: (v: string) =>
        setInputFilters((prev) => ({ ...prev, stakeholderIdentifier: v })),
    },
    {
      label: 'Nombre receptor',
      type: 'autocomplete',
      value: inputFilters.stakeholderName,
      key: 'stakeholderName',
      setter: (v: string) =>
        setInputFilters((prev) => ({ ...prev, stakeholderName: v ?? '' })),
    },
  ];

  const handleFactoring = useCallback(() => {
    const selectedInvoices = displayInvoices.filter((x) =>
      selected.includes(x.id),
    );
    setSelectedDocuments(selectedInvoices);
    setOpenShoppingcart(true);
  }, [displayInvoices, selected]);

  const handleSimulation = useCallback(() => {
    const selectedInvoices = displayInvoices.filter((x) =>
      selected.includes(x.id),
    );
    setSelectedDocuments(selectedInvoices);
    setOpenShoppingcart(true);
  }, [displayInvoices, selected]);

  const setUp = useCallback(() => {
    let newButtonsData: CustomButtonProps[] = [];
    if (validateCompanyTypes(user, 'Accountant')) {
      newButtonsData = [
        {
          action: handleFactoring,
          name: 'Factorizar',
          color: 'primary',
          disabled: !selected.length,
        },
      ];
    }
    if (validateCompanyTypes(user, 'FactoringQuoting')) {
      if (validateCompanyTypes(user, 'FinancingHub')) {
        setOnlySimulation(false);
      } else {
        newButtonsData = [
          {
            action: handleSimulation,
            name: 'Simular',
            color: 'primary',
            disabled: !selected.length,
          },
        ];
      }
    }
    setButtonsData(newButtonsData);
  }, [user, selected, handleFactoring, handleSimulation]);

  useEffect(() => {
    setUp();
  }, [setUp]);

  const onRefresh = useCallback(
    async (_id?: string | number) => {
      await queryClient.invalidateQueries({
        queryKey: ['businessInvoices', businessIdentifier, 'emitted'],
      });
      setUp();
    },
    [queryClient, businessIdentifier, setUp],
  );

  if (isLoading && user?.express)
    return (
      <CenteredCircularProgress type={user?.express ? 'noLayout' : 'layout'} />
    );

  const cantSelectRow = (row: InvoiceRow) => {
    if (row.status !== 'Transable') return true;
    if (blockedFolios.includes(row.folio)) return true;

    if (row.cancelDate !== null) return true;

    return false;
  };

  const canSelect = () => {
    if (validateCompanyTypes(user, 'FinancingHub')) return false;
    return (
      validateCompanyTypes(user, 'Accountant') ||
      validateCompanyTypes(user, 'FactoringQuoting')
    );
  };

  const handleShowBusinessDetails = (rowId: string) => {
    const row = displayInvoices.find((x) => x.id === rowId);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier ?? '',
        name: row.stakeholderName ?? '',
      }),
    );
  };

  if (expressNotEnrolled && user?.express) return <ReEnrollment />;

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <PageHeader.WithTitleAndActions
          iconType="invoice"
          title={'Facturas Emitidas.'}
          subtitle={`Aquí verás las facturas que has emitido. ${user?.express ? 'Selecciona para factorizar.' : ''}`}
          globalSearcherProps={{
            filters,
            triggerSearch,
            dispatch,
            disableSearch: isLoading || isFetching,
          }}
          actionButtons={buttonsData.map((b) => ({
            label: b.name ?? '',
            onClick: b.action,
            color: b.color,
            disabled: b.disabled,
          }))}
        />
        {recentlyCreated && (
          <Typography variant="body1" color="error">
            *Esta empresa fue conectada hace menos de 5 minutos, algunas
            facturas todavía se están cargando...
          </Typography>
        )}
        <TableWithPagination
          tableSize="small"
          exportToFile="xlsx"
          finalSumRow
          dataHeaders={[
            { key: 'folio', label: 'Folio' },
            { key: 'code', label: 'Cód.' },
            {
              key: 'stakeholderName',
              label: 'Receptor',
            },
            {
              key: 'stakeholderIdentifier',
              label: 'Rut receptor',
              ...(!user?.express && {
                handleAction: handleShowBusinessDetails,
                icon: <InfoIcon fontSize="small" />,
              }),
            },

            { key: 'amount', label: 'Monto', type: 'money' },
            {
              key: 'issuedDate',
              label: 'Emisión',
              type: 'date',
              formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
            },
            { key: 'receiptDate', label: 'Recepción', type: 'eight-days-date' },
            {
              key: 'creditNoteAmount',
              label: 'NC',
              type: 'money-colored',
            },
            { key: 'status', label: 'Estado', type: 'status-chip' },
            {
              key: 'cancelDate',
              label: 'Reclamo',
              type: 'date-colored',
            },
            {
              key: 'confirmDate',
              label: 'Acuse',
              type: 'date-colored-ok',
            },
            getCessionHistoryColumnConfig(handleShowCessionHistory),
            {
              key: 'lastCessionDate',
              label: 'Fecha de cesión',
              type: 'date',
              formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
            },
          ]}
          rowsPerPageDefault={20}
          loading={isLoading || isFetching}
          setData={() => {}}
          searchData={searchEmmitedInvoices}
          setPagination={() => {}}
          pagination={data?.pagination ?? DEFAULT_PAGINATION}
          data={displayInvoices}
          selected={canSelect() ? selected : null}
          setSelected={setSelected}
          cantSelectRow={cantSelectRow}
        />
      </Grid>
      {openAddInvoices && (
        <AddInvoicesManually
          open={openAddInvoices}
          setOpen={setOpenAddInvoices}
          business={business}
          setUp={onRefresh}
        />
      )}

      {openShoppingCart && (
        <ShoppingCartFactoring
          selectedDocuments={selectedDocuments}
          setOpenShoppingcart={setOpenShoppingcart}
          openShoppingCart={openShoppingCart}
          setContinueToSimulation={handleContinueToSimulation}
          setSelectedRequest={setSelectedRequest}
          setSelectedDocuments={setSelectedDocuments}
        />
      )}
      {continueToSimulation && (
        <ShoppingDialogRecieved
          openShoppingCart={continueToSimulation}
          setOpenShoppingCart={setContinueToSimulation}
          selectedRequest={selectedRequest}
          setSelectedRequest={setSelectedRequest}
          newRequest
          setSuccessOpen={setSuccessOpen}
          setFailureOpen={setFailureOpen}
          setUp={onRefresh}
          executiveCreating
          shoppingCartType="Revisada"
          onlySimulation={onlySimulation}
        />
      )}

      <CustomAlert
        setOpen={setSuccess}
        open={success}
        title="Exito"
        text={successText}
        severity="success"
      />
      <CustomAlert
        setOpen={setFailed}
        open={failed}
        title="Oops"
        text={failedMessage}
        severity="error"
      />
      <CessionHistoryDialog
        open={openCessionHistoryDialog}
        onClose={() => setOpenCessionHistoryDialog(false)}
        cessions={selectedCessions}
      />
    </Grid>
  );
};

export default BusinessInvoicesEmitted;
